/**
 * L01 — Login page.
 *
 * Adattata da design/stitch-import/html/9b81d8a4f6bf4df09c7e42b740f5116f.html:
 * - RIMOSSO bottone "Accedi con Google" e divider "oppure"
 *   (no SSO in DietaGest — gestionale interno, niente self-registration).
 * - RIMOSSO link footer "Sei un paziente? Accedi…" (i pazienti arrivano
 *   solo da link onboarding L03 firmato, no signup).
 * - "Password dimenticata?" linka a /forgot-password.
 * - Aggiunta nota statica per scoraggiare ticket di supporto:
 *   "Non riesci ad accedere? Contatta l'amministratore."
 *
 * Errori: SEMPRE generici ("Credenziali non valide"), niente leak email.
 */

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  signInWithEmailAndPassword,
  getMultiFactorResolver,
  type MultiFactorResolver,
  type MultiFactorError,
} from 'firebase/auth';
import { auth } from '../../security/firebase';
import { BrandPanel } from '../../components/ui/BrandPanel';
import { MobileLogo } from '../../components/ui/MobileLogo';
import { MfaModal } from '../../components/auth/MfaModal';

const loginSchema = z.object({
  email: z.string().min(1, 'Inserisci l\'email').email('Email non valida'),
  password: z.string().min(1, 'Inserisci la password'),
});

type LoginInput = z.infer<typeof loginSchema>;

export function LoginPage() {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [genericError, setGenericError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [mfaResolver, setMfaResolver] = useState<MultiFactorResolver | null>(
    null
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
    mode: 'onSubmit',
  });

  const onSubmit = async (data: LoginInput) => {
    setGenericError(null);
    setSubmitting(true);
    try {
      await signInWithEmailAndPassword(auth, data.email, data.password);
      navigate('/app', { replace: true });
    } catch (err) {
      const code = (err as { code?: string }).code;
      if (code === 'auth/multi-factor-auth-required') {
        try {
          const resolver = getMultiFactorResolver(
            auth,
            err as MultiFactorError
          );
          const hasTotp = resolver.hints.some((h) => h.factorId === 'totp');
          if (!hasTotp) {
            setGenericError(
              'Nessun secondo fattore TOTP configurato per questo account.'
            );
          } else {
            setMfaResolver(resolver);
          }
        } catch {
          setGenericError(
            'Impossibile avviare la verifica a due fattori. Riprova.'
          );
        }
      } else {
        setGenericError('Credenziali non valide');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleMfaSuccess = () => {
    setMfaResolver(null);
    navigate('/app', { replace: true });
  };

  const handleMfaCancel = () => {
    setMfaResolver(null);
    setGenericError('Verifica a due fattori annullata. Riprova ad accedere.');
  };

  return (
    <div className="min-h-screen flex items-stretch">
      <BrandPanel />
      <section className="flex-1 bg-white flex flex-col items-center justify-center p-6 md:p-margin-desktop">
        <MobileLogo />
        <div className="w-full max-w-[400px]">
          <header className="mb-10 text-center md:text-left">
            <h2 className="font-headline-lg text-headline-lg text-text-main mb-2">
              Accedi al gestionale
            </h2>
            <p className="font-body-md text-text-secondary">
              Inserisci le tue credenziali per continuare
            </p>
          </header>

          {genericError && (
            <div
              role="alert"
              className="mb-6 p-3 rounded-lg bg-error-container text-on-error-container text-body-sm"
            >
              {genericError}
            </div>
          )}

          <form
            noValidate
            className="space-y-6"
            onSubmit={handleSubmit(onSubmit)}
          >
            <div>
              <label
                className="block font-label-sm text-on-surface-variant mb-2"
                htmlFor="email"
              >
                Email professionale
              </label>
              <div className="relative group">
                <span
                  className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-outline group-focus-within:text-primary"
                  aria-hidden="true"
                >
                  mail
                </span>
                <input
                  {...register('email')}
                  id="email"
                  type="email"
                  autoComplete="username"
                  placeholder="Email professionale"
                  aria-invalid={errors.email ? 'true' : 'false'}
                  className="w-full h-12 pl-10 pr-4 rounded-lg border border-border-gray bg-white text-on-surface placeholder:text-outline-variant focus:outline-none focus:border-primary focus:ring-2 focus:ring-magenta-soft transition-all font-body-md"
                />
              </div>
              {errors.email && (
                <p className="mt-1 text-body-sm text-error" role="alert">
                  {errors.email.message}
                </p>
              )}
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <label
                  className="block font-label-sm text-on-surface-variant"
                  htmlFor="password"
                >
                  Password
                </label>
              </div>
              <div className="relative group">
                <span
                  className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-outline group-focus-within:text-primary"
                  aria-hidden="true"
                >
                  lock
                </span>
                <input
                  {...register('password')}
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  placeholder="••••••••"
                  aria-invalid={errors.password ? 'true' : 'false'}
                  className="w-full h-12 pl-10 pr-10 rounded-lg border border-border-gray bg-white text-on-surface placeholder:text-outline-variant focus:outline-none focus:border-primary focus:ring-2 focus:ring-magenta-soft transition-all font-body-md"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={
                    showPassword ? 'Nascondi password' : 'Mostra password'
                  }
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-outline hover:text-on-surface transition-colors"
                >
                  <span
                    className="material-symbols-outlined"
                    aria-hidden="true"
                  >
                    {showPassword ? 'visibility_off' : 'visibility'}
                  </span>
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-body-sm text-error" role="alert">
                  {errors.password.message}
                </p>
              )}
            </div>

            <div className="flex items-center justify-end">
              <Link
                to="/forgot-password"
                className="font-body-sm text-primary font-semibold hover:underline"
              >
                Password dimenticata?
              </Link>
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full h-12 bg-primary text-on-primary font-body-md font-bold rounded-lg shadow-sm hover:opacity-90 active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Accesso in corso…' : 'Accedi'}
            </button>
          </form>

          <p className="mt-12 text-center font-body-sm text-text-secondary">
            Non riesci ad accedere? Contatta l'amministratore.
          </p>
        </div>
      </section>

      <MfaModal
        isOpen={mfaResolver !== null}
        resolver={mfaResolver}
        tier="tier1"
        actionLabel="completare l'accesso"
        onSuccess={handleMfaSuccess}
        onCancel={handleMfaCancel}
      />
    </div>
  );
}

export default LoginPage;
